import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import MediaPreview from 'components/MediaPreview';
import StatusLabel from 'components/status-label/StatusLabel';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';
import LikeContentActionButton from 'components/like-content-action-button/LikeContentActionButton';

import { __t, applyTranslations } from 'utils/language';
import { APPROVAL_STATUS_APPROVED, APPROVAL_STATUS_DECLINED, APPROVAL_STATUS_PENDING } from 'utils/profile';

import './deliverable-media-for-approval.scss';

import type {
  DeliverableAttachment,
  DeliverableAttachmentApprovalStatus,
  DeliverableAttachmentForClientApproval,
  DeliverableClientApproval,
  StatusLabelType
} from 'types';

applyTranslations(require('./deliverable-media-for-approval.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

type OwnProps = {
  showButtons: boolean,
  showStatus: boolean,
  showReset?: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  deliverableId?: number,
  attachment: DeliverableAttachmentForClientApproval,
  contextMenu?: React.ReactNode,
  className?: string,
  onApprovalStatusChanged?: (b: DeliverableAttachment, a: DeliverableAttachmentApprovalStatus) => void
};

type StateProps = {
  clientApprovalData: DeliverableClientApproval | null
}

type Props = OwnProps & StateProps;

const STATUS_LABEL_MAPPING: { [key: string]: StatusLabelType } = {
  [APPROVAL_STATUS_PENDING]: 'warning',
  [APPROVAL_STATUS_APPROVED]: 'success',
  [APPROVAL_STATUS_DECLINED]: 'danger'
};

const msgKey = 'deliverable_media_for_approval';
const COMMON_MSG_KEY = 'collabary_common';

class DeliverableMediaForApproval extends React.PureComponent<Props> {
  static defaultProps = {
    className: ''
  };

  handleApprovalStatusChanged = (event: Event): void => {
    const { attachment, onApprovalStatusChanged } = this.props;
    if (!onApprovalStatusChanged) {
      return;
    }

    event.stopPropagation();

    // @ts-ignore
    const nextStatus = event.currentTarget.dataset.status;

    // @ts-ignore
    onApprovalStatusChanged(attachment, nextStatus);
  };

  handleCancelGallery = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  handleReset = (): void => {
    const { attachment, onApprovalStatusChanged } = this.props;

    if (onApprovalStatusChanged) {
      // @ts-ignore
      onApprovalStatusChanged(attachment, 'pending');
    }
  };

  renderFileName(attachment: DeliverableAttachmentForClientApproval, width: number): React.ReactNode {
    return (
      <span
        className="deliverable-media-for-approval__filename text-overflow-ellipsis"
        title={attachment.fileName}
        style={{ width: `${width}px` }}
      >
        {attachment.fileName}
      </span>
    );
  }

  renderResetIcon(): React.ReactNode {
    return (
      <div className="deliverable-media-for-approval__reset">
        <Tooltip
          message={__t(`${msgKey}.tooltips.reset`)}
          width="auto-compact"
        >
          <SVGDynamicIcon
            name="rotate-left-custom-fg"
            width={16}
            height={14}
            customColor="grey"
            onClick={this.handleReset}
            clickable
          />
        </Tooltip>
      </div>
    );
  }

  renderStatusLabel(): React.ReactNode {
    const { showStatus, attachment } = this.props;

    const approvalStatus = attachment.approvalStatus;

    return approvalStatus && showStatus && (
      <StatusLabel
        size="nano"
        type={STATUS_LABEL_MAPPING[approvalStatus]}
        label={__t(`${msgKey}.labels.${approvalStatus}`)}
      />
    );
  }

  renderFooter() {
    const { showStatus, showButtons, showReset, attachment } = this.props;

    const approvalStatus = attachment.approvalStatus;

    const footerClasses = classnames({
      'deliverable-media-for-approval__footer': true,
      'deliverable-media-for-approval__footer--actions': showButtons,
      'deliverable-media-for-approval__footer--status': showStatus
    });

    return (
      <div
        className={footerClasses}
        onClick={this.handleCancelGallery}
      >
        {
          showButtons &&
            <React.Fragment>
              <div className="deliverable-media-for-approval__button">
                <ActionButton
                  width="small"
                  height="minimum"
                  style="small"
                  color="tertiary"
                  data={{ status: 'declined' }}
                  onClick={this.handleApprovalStatusChanged}
                >
                  {__t(`${COMMON_MSG_KEY}.action_button.decline`)}
                </ActionButton>
              </div>
              <div className="deliverable-media-for-approval__button">
                <ActionButton
                  width="small"
                  height="minimum"
                  style="small"
                  color="primary"
                  data={{ status: 'approved' }}
                  onClick={this.handleApprovalStatusChanged}
                >
                  {__t(`${COMMON_MSG_KEY}.action_button.approve`)}
                </ActionButton>
              </div>
            </React.Fragment>
        }
        {this.renderStatusLabel()}
        {
          showReset && approvalStatus !== 'pending' &&
            this.renderResetIcon()
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { attachment, contextMenu, className, clientApprovalData } = this.props;
    const previewWidth = 183;
    const previewHeight = 183;
    const contextMenuWidth = 45;

    const clientItem = (clientApprovalData?.items || []).find(item => item.assetId === attachment.id);

    return (
      <div className={`deliverable-media-for-approval ${className}`}>
        <div
          className="deliverable-media-for-approval__preview"
          style={{ width: previewWidth, height: previewHeight }}
        >
          {
            clientItem?.favourite && (
              <LikeContentActionButton />
            )
          }
          <MediaPreview
            key={`deliverable-media-attachment-preview-${attachment.id}`}
            attachment={{
              ...attachment,
              format: attachment.mediaType || attachment.assetType
            }}
            width={previewWidth}
            height={previewHeight}
            showPdfFileName={false}
          />
        </div>
        <div
          className="deliverable-media-for-approval__title"
          onClick={this.handleCancelGallery}
        >
          {this.renderFileName(attachment, previewWidth - contextMenuWidth)}
          {contextMenu}
        </div>
        {this.renderFooter()}
      </div>
    );
  }
}

const mapStateToProps = ({ deliverableContentApproval }, ownProps: OwnProps): StateProps => ({
  clientApprovalData: ownProps.deliverableId ? deliverableContentApproval.clientApprovalData[ownProps.deliverableId] : null
});

export default connect(mapStateToProps)(DeliverableMediaForApproval);
